import { useState } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { showNotification } from "context/notification.service";
import { serviceboxSyncApi, type ServiceBoxRecentRdv } from "@services/api/serviceboxSync";
import { navigateTo } from "context/navigation.service";

type PeriodOption = { label: string; days: number; direction: string };

const PERIOD_OPTIONS: PeriodOption[] = [
  { label: "7 derniers jours", days: 7, direction: "past" },
  { label: "14 derniers jours", days: 14, direction: "past" },
  { label: "30 derniers jours", days: 30, direction: "past" },
  { label: "7 prochains jours", days: 7, direction: "future" },
  { label: "14 prochains jours", days: 14, direction: "future" },
  { label: "30 prochains jours", days: 30, direction: "future" },
];

const FIELD_LABELS: Record<string, string> = {
  reception: "Réception",
  restitution: "Restitution",
  reception_category: "Poste réception",
  restitution_category: "Poste restitution",
  num: "N° OR",
  interventions: "Interventions",
  status: "Statut",
};

export const ServiceBoxSync = () => {
  const [loading, setLoading] = useState(false);
  const [rdvs, setRdvs] = useState<ServiceBoxRecentRdv[] | null>(null);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [existingCount, setExistingCount] = useState(0);
  const [modifiedCount, setModifiedCount] = useState(0);
  const [filter, setFilter] = useState<"all" | "new" | "existing" | "modified">("new");
  const [periodIdx, setPeriodIdx] = useState(3); // default: 7 prochains jours
  const [importingAll, setImportingAll] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [updatingAll, setUpdatingAll] = useState(false);
  const [reSyncAtelier, setReSyncAtelier] = useState(false);

  const period = PERIOD_OPTIONS[periodIdx];

  const fetchRdvs = async () => {
    setLoading(true);
    try {
      const data = await serviceboxSyncApi.getRecentRdvs(period.days, period.direction);
      setRdvs(data.rdvs);
      setTotal(data.total);
      setNewCount(data.new_count);
      setExistingCount(data.existing_count);
      setModifiedCount(data.modified_count ?? 0);
    } catch (e: any) {
      showNotification({
        type: "error",
        message: e?.response?.data?.detail || "Erreur lors de la récupération des RDV",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportAll = async () => {
    if (!rdvs) return;
    const toImport = rdvs.filter((r) => r.state === "new");
    if (toImport.length === 0) return;

    setImportingAll(true);
    setImportProgress({ done: 0, total: toImport.length });

    try {
      const result = await serviceboxSyncApi.batchImport(toImport);

      // Update local state with results
      const successMap = new Map<string, string>();
      for (const r of result.results) {
        if (r.success && r.ordre_id) {
          successMap.set(r.dossier_id, r.ordre_id);
        }
      }

      setRdvs((prev) =>
        prev?.map((r) => {
          const ordreId = successMap.get(r.dossier_id);
          return ordreId ? { ...r, exists_in_heric: true, heric_ordre_id: ordreId } : r;
        }) ?? null
      );

      setNewCount((c) => c - result.success_count);
      setExistingCount((c) => c + result.success_count);
      setImportProgress({ done: result.total, total: result.total });

      if (result.error_count === 0) {
        const jobsMsg = result.total_jobs > 0 ? ` + ${result.total_jobs} jobs atelier` : "";
        showNotification({ type: "success", message: `${result.success_count} RDV importés avec succès${jobsMsg}` });
      } else {
        showNotification({
          type: "error",
          message: `${result.success_count} importés, ${result.error_count} erreurs`,
        });
      }
    } catch (e: any) {
      showNotification({
        type: "error",
        message: e?.response?.data?.detail || "Erreur lors de l'import",
      });
    } finally {
      setImportingAll(false);
    }
  };

  const handleUpdateAll = async () => {
    if (!rdvs) return;
    const toUpdate = rdvs.filter((r) => r.state === "modified");
    if (toUpdate.length === 0) return;

    setUpdatingAll(true);
    try {
      const result = await serviceboxSyncApi.batchUpdate(toUpdate, reSyncAtelier);

      // Mark successfully updated RDVs as "existing" with no changed_fields
      const successOrdreIds = new Set(
        result.results.filter((r) => r.success).map((r) => r.ordre_id),
      );
      setRdvs((prev) =>
        prev?.map((r) =>
          r.heric_ordre_id && successOrdreIds.has(r.heric_ordre_id)
            ? { ...r, state: "existing", changed_fields: [] }
            : r,
        ) ?? null,
      );
      setModifiedCount((c) => c - result.success_count);
      setExistingCount((c) => c + result.success_count);

      if (result.error_count === 0) {
        const jobsMsg = result.total_jobs_resynced > 0
          ? ` (${result.total_jobs_resynced} jobs atelier resynchronisés)`
          : "";
        showNotification({
          type: "success",
          message: `${result.success_count} RDV mis à jour${jobsMsg}`,
        });
      } else {
        showNotification({
          type: "error",
          message: `${result.success_count} mis à jour, ${result.error_count} erreurs`,
        });
      }
    } catch (e: any) {
      showNotification({
        type: "error",
        message: e?.response?.data?.detail || "Erreur lors de la mise à jour",
      });
    } finally {
      setUpdatingAll(false);
    }
  };

  const filteredRdvs = rdvs
    ? rdvs.filter((r) => {
        if (filter === "new") return r.state === "new";
        if (filter === "existing") return r.state === "existing";
        if (filter === "modified") return r.state === "modified";
        return true;
      })
    : [];

  return (
    <div className="flex flex-col p-8 gap-6 max-w-[1200px]">
      <div>
        <Text1>Synchronisation ServiceBox</Text1>
        <div className="mt-1 text-gray-500 text-sm">
          Récupère les RDV depuis ServiceBox et identifie ceux qui n'existent pas encore dans Heric.
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Période :</label>
          <select
            value={periodIdx}
            onChange={(e) => setPeriodIdx(Number(e.target.value))}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            {PERIOD_OPTIONS.map((opt, i) => (
              <option key={i} value={i}>{opt.label}</option>
            ))}
          </select>
        </div>
        <Button
          label={loading ? "Chargement..." : "Récupérer les RDV"}
          onClick={fetchRdvs}
          loading={loading}
          disabled={loading}
        />
      </div>

      {rdvs !== null && (
        <>
          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setFilter("new")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === "new"
                    ? "bg-white shadow text-blue-600 font-medium"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Nouveaux ({newCount})
              </button>
              <button
                onClick={() => setFilter("modified")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === "modified"
                    ? "bg-white shadow text-orange-600 font-medium"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Modifiés ({modifiedCount})
              </button>
              <button
                onClick={() => setFilter("existing")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === "existing"
                    ? "bg-white shadow text-blue-600 font-medium"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Existants ({existingCount})
              </button>
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === "all"
                    ? "bg-white shadow text-blue-600 font-medium"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Tous ({total})
              </button>
            </div>
            </div>
            <div className="flex items-center gap-3">
              {filter === "modified" && modifiedCount > 0 && (
                <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reSyncAtelier}
                    onChange={(e) => setReSyncAtelier(e.target.checked)}
                    disabled={updatingAll}
                  />
                  Re-synchroniser planning atelier
                </label>
              )}
              {modifiedCount > 0 && (
                <Button
                  label={updatingAll ? `Mise à jour en cours...` : `Mettre à jour tout (${modifiedCount})`}
                  onClick={handleUpdateAll}
                  loading={updatingAll}
                  disabled={updatingAll || importingAll}
                />
              )}
              {newCount > 0 && (
                <Button
                  label={importingAll ? `Import en cours (${newCount} RDV)...` : `Importer tout (${newCount})`}
                  onClick={handleImportAll}
                  loading={importingAll}
                  disabled={importingAll || updatingAll}
                />
              )}
            </div>
          </div>

          {filteredRdvs.length === 0 ? (
            <div className="text-gray-500 text-sm py-8 text-center">
              {filter === "new"
                ? "Aucun nouveau RDV détecté"
                : filter === "modified"
                ? "Aucun RDV modifié"
                : filter === "existing"
                ? "Aucun RDV existant"
                : "Aucun RDV trouvé"}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Véhicule</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Immat.</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Réception</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Restitution</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Interventions</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Avancement</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRdvs.map((rdv) => {
                    const recDiff = rdv.changed_fields.find((d) => d.field === "reception");
                    const restDiff = rdv.changed_fields.find((d) => d.field === "restitution");
                    const diffSummary = rdv.changed_fields
                      .map((d) => {
                        const label = FIELD_LABELS[d.field] || d.field;
                        return `${label}: ${d.old || "—"} → ${d.new || "—"}`;
                      })
                      .join("\n");
                    return (
                    <tr
                      key={rdv.dossier_id}
                      className={`border-b last:border-b-0 hover:bg-gray-50 ${
                        rdv.state === "new"
                          ? "bg-green-50/50"
                          : rdv.state === "modified"
                          ? "bg-orange-50/50"
                          : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        {rdv.state === "new" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Nouveau
                          </span>
                        ) : rdv.state === "modified" ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"
                            title={diffSummary}
                          >
                            Modifié
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            Existant
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{rdv.client_name}</td>
                      <td className="px-4 py-3 text-gray-600">{rdv.modele}</td>
                      <td className="px-4 py-3 font-mono text-xs">{rdv.plate}</td>
                      <td
                        className={`px-4 py-3 ${recDiff ? "text-orange-700" : "text-gray-600"}`}
                        title={recDiff ? `Heric: ${recDiff.old || "—"}` : undefined}
                      >
                        {rdv.reception_date && (
                          <span>{rdv.reception_date} {rdv.reception_heure}</span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 ${restDiff ? "text-orange-700" : "text-gray-600"}`}
                        title={restDiff ? `Heric: ${restDiff.old || "—"}` : undefined}
                      >
                        {rdv.restitution_date && (
                          <span>{rdv.restitution_date} {rdv.restitution_heure}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[250px]">
                        <div className="truncate" title={rdv.interventions.join(", ")}>
                          {rdv.interventions.join(", ") || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{rdv.status}</td>
                      <td className="px-4 py-3">
                        {rdv.exists_in_heric && rdv.heric_ordre_id && (
                          <SecondaryButton
                            label="Voir"
                            variant="small"
                            onClick={() => navigateTo({ route: `/ordre/${rdv.heric_ordre_id}` })}
                          />
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
